import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Check } from "lucide-react";
import Logo from "@/shared/components/branding/Logo";
import AsyncButton from "@/shared/components/feedback/AsyncButton";
import { useAuth } from "../../../lib/auth";
import { isMockApi } from "@/lib/api";

export default function RegisterPage() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const minimumPasswordLength = isMockApi ? 6 : 8;
  const passwordsMatch = confirmPassword.length > 0 && password === confirmPassword;
  const passwordsMismatch = confirmPassword.length > 0 && !passwordsMatch;
  const canSubmit = passwordsMatch && password.length >= minimumPasswordLength;

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < minimumPasswordLength) {
      toast.error(`Password must be at least ${minimumPasswordLength} characters.`);
      return;
    }
    if (password !== confirmPassword) {
      toast.error("Passwords do not match.");
      return;
    }
    setBusy(true);
    try {
      await register(email.trim(), password);
      toast.success("Account created. Let's set up your business.");
      nav("/onboarding");
    } catch (err) {
      toast.error(err.message || "Registration failed");
    } finally {
      setBusy(false);
    }
  };

  const perks = [
    "Storefront hosted at /store/your-brand",
    "Take orders & bookings — no code",
    "Insights dashboard included",
    "Free while in MVP",
  ];

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <Logo />
          <h1 className="mt-10 text-3xl font-display font-medium tracking-tight" data-testid="register-title">
            Start your storefront
          </h1>
          <p className="mt-2 text-ink-secondary">
            A free vendor account. No credit card required.
          </p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="register-form">
            <div>
              <label className="text-xs uppercase tracking-widest text-ink-muted" htmlFor="email">Work email</label>
              <input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@business.com"
                className="input-field mt-2"
                data-testid="register-email-input"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-ink-muted" htmlFor="password">Password</label>
              <input
                id="password"
                type="password"
                required
                minLength={minimumPasswordLength}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={`Minimum ${minimumPasswordLength} characters`}
                className="input-field mt-2"
                data-testid="register-password-input"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-ink-muted" htmlFor="confirm-password">Confirm password</label>
              <input
                id="confirm-password"
                type="password"
                required
                minLength={minimumPasswordLength}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter your password"
                aria-invalid={passwordsMismatch}
                aria-describedby="confirm-password-status"
                className={`input-field mt-2 ${
                  passwordsMatch ? "!border-success focus:!border-success" : passwordsMismatch ? "!border-red-500 focus:!border-red-500" : ""
                }`}
                data-testid="register-confirm-password-input"
              />
              <p
                id="confirm-password-status"
                className={`mt-1 text-xs ${passwordsMatch ? "text-success" : passwordsMismatch ? "text-red-400" : "text-ink-muted"}`}
                aria-live="polite"
                data-testid="register-confirm-password-status"
              >
                {passwordsMatch ? "Passwords match." : passwordsMismatch ? "Passwords do not match." : "Re-enter your password to confirm it."}
              </p>
            </div>

            <AsyncButton
              type="submit"
              disabled={!canSubmit}
              className="btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid="register-submit-btn"
              pending={busy}
              pendingLabel="Creating account…"
            >
              <>Create account <ArrowRight className="h-4 w-4" /></>
            </AsyncButton>
          </form>

          <p className="mt-8 text-sm text-ink-secondary">
            Already have an account?{" "}
            <Link to="/login" className="text-brand hover:text-brand-hover font-medium transition-colors" data-testid="register-goto-login">
              Sign in
            </Link>
          </p>
        </div>
      </div>

      <div className="hidden lg:flex relative overflow-hidden border-l border-line items-center justify-center p-12">
        <div className="absolute inset-0 grain" />
        <div className="absolute inset-0 bg-gradient-to-br from-brand/15 via-transparent to-salon/10" />
        <div className="relative max-w-md">
          <div className="tag mb-6 text-brand border-brand/30 bg-brand-soft">What you get</div>
          <h2 className="text-3xl font-display font-medium tracking-tight">
            A production-ready storefront in minutes.
          </h2>
          <ul className="mt-8 space-y-3">
            {perks.map((p) => (
              <li key={p} className="flex items-start gap-3 text-ink-secondary">
                <span className="mt-1 h-5 w-5 rounded-full bg-success/10 border border-success/30 grid place-items-center flex-shrink-0">
                  <Check className="h-3 w-3 text-success" />
                </span>
                {p}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
